/**
 * De twee speelfiguren: de Ridder en de Molen.
 *
 * Wie de sterkste riddermacht heeft krijgt de ridderfiguur, wie de grootste
 * handelsmacht heeft de molenfiguur; beide zijn een overwinningspunt waard
 * (handboek p. 11 en 13). Bij gelijkspel heeft **niemand** de figuur — dat staat zo in
 * de fysieke regels ("If there is a tie, neither player controls the token") en het
 * volgt ook uit de logica: de figuur wisselt pas als iemand écht meer heeft.
 *
 * Twee kaarten tellen niet gewoon op maar vermenigvuldigen:
 * - **Smederij**: "Voor iedere ridder krijg je er een sterktepunt bij."
 * - **Haven**: "Voor elke vloot krijg je er een extra handelspunt bij."
 *
 * **De molen vereist een stad.** Wie alleen dorpen heeft, krijgt de molenfiguur niet —
 * ook niet met de meeste handelspunten. Het handboek noemt die eis niet (p. 11 zegt
 * alleen "wie over de grootste handelsmacht beschikt"), maar dit is een bewuste keuze
 * van de eigenaar van dit project. Zonder stad geen molen; met steden telt gewoon wie
 * de meeste punten heeft.
 */
import { BASIC_CARDS, type Seat } from '@catan/contracts';
import type { GameState, PlayerState } from './state.js';

const byId = new Map(BASIC_CARDS.map((c) => [c.id, c]));

const nameOf = (cardId: number): string => byId.get(cardId)?.name ?? '';

/** Alle uitbreidingen die deze speler gebouwd heeft. */
export function builtCards(player: PlayerState): number[] {
  return player.principality.buildings.flatMap((b) => [...b.expansions.boven, ...b.expansions.onder]);
}

/**
 * Riddermacht: de sterkte van alle ridders, plus één per ridder voor elke smederij.
 */
export function knightPower(player: PlayerState): number {
  const gebouwd = builtCards(player);
  const ridders = gebouwd.filter((id) => (byId.get(id)?.strength ?? 0) > 0);
  const smederijen = gebouwd.filter((id) => nameOf(id) === 'Smederij').length;
  const basis = ridders.reduce((sum, id) => sum + (byId.get(id)?.strength ?? 0), 0);
  return basis + ridders.length * smederijen;
}

/**
 * Handelsmacht: de handelspunten van alle gebouwen, plus één per vloot voor elke haven.
 */
export function commercePower(player: PlayerState): number {
  const gebouwd = builtCards(player);
  const vloten = gebouwd.filter((id) => nameOf(id).endsWith('vloot')).length;
  const havens = gebouwd.filter((id) => nameOf(id) === 'Haven').length;
  const basis = gebouwd.reduce((sum, id) => sum + (byId.get(id)?.commercePoints ?? 0), 0);
  return basis + vloten * havens;
}

/** Toernooipunten: de som over alle ridders. */
export function tournamentPower(player: PlayerState): number {
  return builtCards(player).reduce((sum, id) => sum + (byId.get(id)?.tournamentPoints ?? 0), 0);
}

/** Wie heeft er meer? Gelijk betekent: niemand. */
function leader(a: number, b: number): Seat | undefined {
  if (a === b) return undefined;
  return a > b ? 0 : 1;
}

/** Heeft deze speler minstens één stad? Voorwaarde voor de molenfiguur. */
export function hasCity(player: PlayerState): boolean {
  return player.principality.buildings.some((b) => b.kind === 'stad');
}

/**
 * Wie krijgt de molen?
 *
 * Alleen wie een stad heeft, doet mee. Heeft één van beiden er geen, dan telt hij niet
 * mee en gaat de figuur naar de ander — mits die zelf punten heeft.
 */
function windmillLeader(state: GameState): Seat | undefined {
  const meedoen = ([0, 1] as const).filter((seat) => hasCity(state.players[seat]));
  if (meedoen.length === 0) return undefined;
  if (meedoen.length === 1) {
    const enige = meedoen[0] as Seat;
    return commercePower(state.players[enige]) > 0 ? enige : undefined;
  }
  return leader(commercePower(state.players[0]), commercePower(state.players[1]));
}

export interface TokenChange {
  readonly knight: Seat | undefined;
  readonly windmill: Seat | undefined;
  /** Meldingen voor het verslag, alleen als er iets veranderde. */
  readonly log: readonly string[];
}

/**
 * Herberekent wie de figuren heeft. Roep dit aan na elke zet die iets kan bouwen of
 * afbreken — de figuur mag geen beurt achterlopen, want hij telt mee voor de winst.
 */
export function updateTokens(state: GameState): TokenChange {
  const knight = leader(knightPower(state.players[0]), knightPower(state.players[1]));
  const windmill = windmillLeader(state);
  const log: string[] = [];

  if (knight !== state.knightToken) {
    log.push(
      knight === undefined
        ? 'de riddermacht is gelijk — niemand heeft de ridderfiguur'
        : `${state.players[knight].name} heeft nu de sterkste riddermacht`,
    );
  }
  if (windmill !== state.windmillToken) {
    log.push(
      windmill === undefined
        ? 'de handelsmacht is gelijk — niemand heeft de molenfiguur'
        : `${state.players[windmill].name} heeft nu de grootste handelsmacht`,
    );
  }
  return { knight, windmill, log };
}
